import api from 'services/client/api';
import { ApiRequestTimeoutError } from 'services/client/ApiService';

/** Only discovery and quote reads use this helper. Payment submissions never do. */
export const offerRead = async <T>(
  endpoint: string,
  options: {
    payload?: object;
    signal?: AbortSignal;
    current?: () => boolean;
    timeoutMs?: number;
  } = {},
): Promise<T> => {
  const { current = () => true, signal, ...request } = options;
  if (
    request.payload &&
    !['quote', 'quote-return', 'resolve', 'status', 'status-return'].includes(
      (request.payload as { action?: string }).action || '',
    )
  )
    throw new Error('Only offer reads can retry.');
  const active = () => !signal?.aborted && current();
  for (let attempt = 0; ; attempt++) {
    if (!active()) throw new DOMException('Offer closed', 'AbortError');
    if (attempt > 0 && typeof document !== 'undefined' && document.visibilityState === 'hidden')
      throw new DOMException('Offer hidden', 'AbortError');
    try {
      return await (request.payload ? api.post : api.get).call(api, endpoint, {
        ...request,
        signal,
        timeoutMs: request.timeoutMs ?? 20000,
        includeErrorStatus: true,
      });
    } catch (error) {
      const status =
        (error as { httpStatus?: number })?.httpStatus ??
        (error as { status?: number; statusCode?: number })?.status ??
        (error as { statusCode?: number })?.statusCode;
      const transient =
        error instanceof TypeError ||
        error instanceof ApiRequestTimeoutError ||
        (typeof status === 'number' && status >= 500);
      if (!active()) throw new DOMException('Offer closed', 'AbortError');
      if (
        !transient ||
        attempt >= 2 ||
        (typeof navigator !== 'undefined' && navigator.onLine === false)
      )
        throw error;
      await new Promise<void>((resolve, reject) => {
        const aborted = () => {
          clearTimeout(timer);
          reject(new DOMException('Offer closed', 'AbortError'));
        };
        const timer = setTimeout(() => {
          signal?.removeEventListener('abort', aborted);
          resolve();
        }, [500, 1500][attempt] + Math.floor(Math.random() * 200));
        signal?.addEventListener('abort', aborted, { once: true });
        if (signal?.aborted) aborted();
      });
    }
  }
};

export const offerFailureCode = (error: unknown) => (error as { code?: string } | null)?.code;
export const shouldRefreshOffer = (error: unknown) =>
  ['QUOTE_EXPIRED', 'PLAN_CHANGED', 'BILLING_CHANGED', 'OFFER_REFRESH'].includes(
    offerFailureCode(error) || '',
  );
